<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import {
  evaluateCustomSlaStatus,
  formatCountdown,
  buildCustomSlaHistory,
} from 'dashboard/helper/customSlaStatus';
import {
  hasReachedAlertPercentage,
  shouldAlertOnce,
  playCustomSlaAlertSound,
  showCustomSlaDesktopNotification,
} from 'dashboard/helper/customSlaAlertHelper';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import CustomSlaPopoverCard from './CustomSlaPopoverCard.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showExtendedInfo: {
    type: Boolean,
    default: false,
  },
  parentWidth: {
    type: Number,
    default: 1000,
  },
});

// This design system does not define distinct yellow/orange/green tokens (only
// blue/ruby/amber/teal/gray/violet), so the 4 required tiers are mapped onto the
// closest available shades: teal for "on track", two amber depths for the 50%/75%
// warning steps, and ruby for breached — rather than introducing new theme colors.
const TIER_COLOR_CLASSES = {
  green: 'text-n-teal-11',
  yellow: 'text-n-amber-9',
  orange: 'text-n-amber-11',
  red: 'text-n-ruby-11',
};

const TICK_INTERVAL = 1000;
const { t } = useI18n();
const store = useStore();
const { uiSettings } = useUISettings();

const timer = ref(null);
const status = ref({
  hasActiveCountdown: false,
  tier: null,
  type: null,
  remainingSeconds: null,
  elapsedSeconds: null,
  actualSeconds: null,
  isBreached: false,
  breachType: null,
  windowStart: null,
});

const accountId = computed(() => store.getters.getCurrentAccountId);
const isFeatureEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    accountId.value,
    FEATURE_FLAGS.CUSTOM_SLA
  )
);

const appliedSla = computed(() => props.chat?.custom_applied_sla);
const customSlaEvents = computed(() => props.chat?.custom_sla_events || []);
const hasCustomSlaThreshold = computed(
  () => isFeatureEnabled.value && !!appliedSla.value
);

const currentUserId = computed(() => store.getters.getCurrentUserID);
const isAssignedToMe = computed(
  () => props.chat?.meta?.assignee?.id === currentUserId.value
);
const customSlaAlertsEnabled = computed(
  () => !!uiSettings.value.custom_sla_alerts_enabled
);
const customSlaAlertPercentage = computed(
  () => uiSettings.value.custom_sla_alert_percentage || 50
);

const statusTextClass = computed(
  () => TIER_COLOR_CLASSES[status.value.tier] || 'text-n-slate-11'
);

const typeLabelKey = computed(() => status.value.type?.toUpperCase());

// The live countdown while a threshold is actively running (including its "overdue"
// wording once running past the deadline); once resolved, a resolution-specific
// Met/Breached readout (status.type is only ever 'resolution' outside the active-
// countdown case — see evaluateCustomSlaStatus). Any other fulfilled-but-not-resolved
// state (e.g. first response answered while still open) shows nothing.
const statusText = computed(() => {
  if (!typeLabelKey.value) return '';

  const typeLabel = t(
    `CONVERSATION.HEADER.CUSTOM_SLA_STATUS.${typeLabelKey.value}`
  );

  if (status.value.hasActiveCountdown) {
    const clock = formatCountdown(status.value.remainingSeconds);
    return status.value.remainingSeconds < 0
      ? t('CONVERSATION.HEADER.CUSTOM_SLA_STATUS.OVERDUE', {
          type: typeLabel,
          clock,
        })
      : t('CONVERSATION.HEADER.CUSTOM_SLA_STATUS.COUNTDOWN', {
          type: typeLabel,
          clock,
        });
  }

  return status.value.isBreached
    ? t('CONVERSATION.HEADER.CUSTOM_SLA_STATUS.RESOLUTION_BREACHED')
    : t('CONVERSATION.HEADER.CUSTOM_SLA_STATUS.RESOLUTION_MET');
});

// Mirrors SLACardLabel's `parentWidth`-gated fallback: when the conversation header is too
// narrow to fit the full sentence (same panel can be narrow even on a large screen, depending
// on which side panels are open, or simply due to browser zoom), fall back to just the
// countdown clock — there's no compact numeric equivalent for the resolved Met/Breached
// readout, so the icon alone carries that state instead of overflowing the header.
const compactStatusText = computed(() =>
  status.value.hasActiveCountdown
    ? formatCountdown(status.value.remainingSeconds)
    : ''
);

// `parentWidth` is 0 for an instant right after (re)mount, before `useElementSize`'s
// ResizeObserver takes its first real measurement (e.g. navigating away from and back to
// the conversation view) — treat that as "not measured yet" and default to the full text
// rather than misreading it as an extremely narrow header and showing the bare clock with
// no indication of which threshold it's counting down.
const shouldShowFullText = computed(
  () =>
    !props.showExtendedInfo ||
    props.parentWidth === 0 ||
    props.parentWidth > 650
);

const history = computed(() =>
  buildCustomSlaHistory({
    appliedSla: appliedSla.value,
    events: customSlaEvents.value,
  })
);

// The next-response met/missed counts render as two always-shown lines (defaulting to 0)
// whenever the policy configures a next-response threshold, so the popover must be
// reachable even when there are zero events and zero once-only-threshold history entries.
const hasNextResponseThreshold = computed(
  () => appliedSla.value?.sla_next_response_time_threshold != null
);

const showPopoverCard = computed(
  () =>
    props.showExtendedInfo &&
    (customSlaEvents.value.length > 0 ||
      history.value.length > 0 ||
      hasNextResponseThreshold.value)
);

// Fires the desktop notification + sound once per window, only for conversations
// assigned to the current agent, only while enabled in the agent's own profile settings
// (see design.md's percentage-alert decision — this is a personal preference, not an
// account-wide policy setting), and only while the dashboard tab is open (there is no
// backend job driving this, since a 5-minute periodic tick can't precisely catch a
// percentage crossing on a short-lived threshold).
const maybeTriggerPercentageAlert = () => {
  if (!customSlaAlertsEnabled.value || !isAssignedToMe.value) return;
  if (!status.value.hasActiveCountdown) return;
  if (
    !hasReachedAlertPercentage(
      status.value.progress,
      customSlaAlertPercentage.value
    )
  ) {
    return;
  }

  const windowKey = `${appliedSla.value.id}-${status.value.type}-${status.value.windowStart}`;
  if (!shouldAlertOnce(windowKey)) return;

  const typeLabel = t(
    `CONVERSATION.HEADER.CUSTOM_SLA_STATUS.${status.value.type?.toUpperCase()}`
  );
  playCustomSlaAlertSound();
  showCustomSlaDesktopNotification({
    title: t('CUSTOM_SLA.ALERT.NOTIFICATION_TITLE', {
      ticket: props.chat?.id,
    }),
    body: t('CUSTOM_SLA.ALERT.NOTIFICATION_BODY', {
      type: typeLabel,
      percentage: customSlaAlertPercentage.value,
    }),
    tag: windowKey,
    url: `${window.location.origin}/app/accounts/${accountId.value}/conversations/${props.chat?.id}`,
  });
};

const updateStatus = () => {
  status.value = evaluateCustomSlaStatus({
    conversation: props.chat,
    appliedSla: appliedSla.value,
    events: customSlaEvents.value,
  });
  maybeTriggerPercentageAlert();
};

const createTimer = () => {
  timer.value = setTimeout(() => {
    updateStatus();
    createTimer();
  }, TICK_INTERVAL);
};

onMounted(() => {
  updateStatus();
  createTimer();
});

onUnmounted(() => {
  if (timer.value) {
    clearTimeout(timer.value);
  }
});

watch(() => props.chat, updateStatus);

defineExpose({ hasCustomSlaThreshold });
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="hasCustomSlaThreshold && statusText"
    class="relative flex items-center cursor-pointer min-w-fit group gap-1"
  >
    <fluent-icon
      size="12"
      icon="alert"
      type="outline"
      icon-lib="lucide"
      class="flex-shrink-0"
      :class="statusTextClass"
    />
    <span
      v-if="shouldShowFullText"
      class="text-xs font-medium tabular-nums"
      :class="statusTextClass"
    >
      {{ statusText }}
    </span>
    <span
      v-else-if="compactStatusText"
      class="text-xs font-medium tabular-nums"
      :class="statusTextClass"
    >
      {{ compactStatusText }}
    </span>
    <CustomSlaPopoverCard
      v-if="showPopoverCard"
      :custom-sla-missed-events="customSlaEvents"
      :applied-sla="appliedSla"
      class="start-0 xl:start-auto xl:end-0 top-5 hidden group-hover:flex"
    />
  </div>
</template>
